// eslint-disable-next-line jira/restricted/react
import React, { PureComponent, type ComponentType } from 'react';
import { Observable } from 'rxjs/Observable';
import { Subject } from 'rxjs/Subject';
import 'rxjs/add/observable/empty';
import 'rxjs/add/operator/catch';
import 'rxjs/add/operator/debounceTime';
import 'rxjs/add/operator/switchMap';
import log from '@atlassian/jira-servicedesk-automation-common/src/log';
import type { ErrorResponse } from '@atlassian/jira-servicedesk-common/src/utils/fetch';
import type { ProjectRunAsUser } from '../../../../../model';
import type { RestRunAsUserOption } from '../../../../../rest/summary/run-as-user/get/types';
import getRunAsUserOptions from '../../../../../services/run-as-user/get';
import type { State as SummaryState } from '../../../../../state/types';
import type { SelectedUser } from './user-picker/view';

export type Props = {
	isStoredUserInvalid: boolean;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	UserPickerComponent: ComponentType<any>;
	summaryState: SummaryState;
	storedRunAsUser: ProjectRunAsUser;
	onSelect: (arg1: SelectedUser) => void;
};

type State = {
	isOpen: boolean;
	isLoading: boolean;
	displayInvalidError: boolean;
	selectedUser: SelectedUser;
	runAsUserOptions: RestRunAsUserOption[];
	filter: string | null;
};

const LOCATION = 'automation.summary.header.settings-dialog.content.user-picker';
const AUTOCOMPLETE_DEBOUNCE_TIME_MS = 300;

// eslint-disable-next-line jira/react/no-class-components
class StatefulUserPicker extends PureComponent<Props, State> {
	static formatSelectedUser(runAsUser: ProjectRunAsUser): SelectedUser {
		return {
			displayName: runAsUser.userDisplayName,
			accountId: runAsUser.accountId,
		};
	}

	constructor(props: Props) {
		super(props);
		this.autocompleteSubject = new Subject();
	}

	state = {
		isOpen: false,
		isLoading: false,
		displayInvalidError: this.props.isStoredUserInvalid,
		// go/jfe-eslint
		selectedUser: StatefulUserPicker.formatSelectedUser(this.props.storedRunAsUser),
		runAsUserOptions: [],
		filter: this.props.storedRunAsUser.userDisplayName || null,
	};

	componentDidMount() {
		this.autocompleteSubject
			.debounceTime(AUTOCOMPLETE_DEBOUNCE_TIME_MS)
			.switchMap((filter) =>
				getRunAsUserOptions(this.props.summaryState, filter).catch((error) =>
					this.handleFetchRunAsUserOptionsError(error),
				),
			)
			.subscribe((runAsUserOptions) => this.setState({ runAsUserOptions, isLoading: false }));
	}

	onSelect = (selectedUser: SelectedUser) => {
		// When a new user is selected, hide the invalid user error message (if any)
		this.setState({ selectedUser, displayInvalidError: false });
		this.props.onSelect(selectedUser);
	};

	onFilterChange = (filter: string) => {
		this.setState({ filter, isLoading: true });
		this.autocompleteSubject.next(filter);
	};

	onMenuOpen = () => {
		this.setState({ isOpen: true });
	};

	onMenuClose = () => {
		this.setState({ isOpen: false });
	};

	autocompleteSubject: Subject<string>;

	handleFetchRunAsUserOptionsError(error: ErrorResponse) {
		const { response } = error;
		const reasonKey = (response && response.reasonKey) || '(unknown)';
		const reasonCode = (response && response.reasonCode) || '(unknown)';
		log.safeWarnWithoutCustomerData(
			LOCATION,
			`Auto-complete search request failed, reasonKey: ${reasonKey}, reasonCode: ${reasonCode}`,
		);
		this.setState({ isLoading: false, isOpen: false });

		return Observable.empty<never>();
	}

	renderFilter() {
		const { isOpen, filter, selectedUser } = this.state;
		if (isOpen) {
			return filter;
		}
		return selectedUser.displayName || null;
	}

	render() {
		const { displayInvalidError, selectedUser, runAsUserOptions, isOpen, isLoading } = this.state;
		const { UserPickerComponent, isStoredUserInvalid } = this.props;

		return (
			<UserPickerComponent
				{...this.props}
				isOpen={isOpen}
				isLoading={isLoading}
				selectedUser={selectedUser}
				runAsUserOptions={runAsUserOptions}
				displayInvalidError={isStoredUserInvalid && displayInvalidError}
				onSelect={this.onSelect}
				onFilterChange={this.onFilterChange}
				onMenuOpen={this.onMenuOpen}
				onMenuClose={this.onMenuClose}
			/>
		);
	}
}

export default StatefulUserPicker;
